import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import { SocialMessage, SocialProfile, SocialThread } from '../social-feed/models/social-feed.model';

/**
 * Direct messages — one-on-one only, no group chat.
 *
 * Both routes ('/social-messages' and '/social-messages/:userId') point at
 * this one component: no id shows the inbox (every thread you've got, newest
 * first); an id shows that conversation. Switching between them re-enters
 * ngOnInit's paramMap subscription rather than swapping components, so
 * navigating from the inbox to a thread (or back) doesn't reset the whole
 * page shell.
 */
@Component({
  selector: 'app-social-messages',
  templateUrl: './social-messages.component.html',
  styleUrls: ['./social-messages.component.scss'],
  standalone: false,
})
export class SocialMessagesComponent implements OnInit, OnDestroy {
  currentUserId: number | null = null;

  // ---- inbox ----
  threads: SocialThread[] = [];
  threadsLoading = false;
  threadsPage = 1;
  hasMoreThreads = false;
  loadingMoreThreads = false;

  // ---- an open thread ----
  activeUserId: number | null = null;
  /** undefined until the first paramMap reading; see the ngOnInit subscription. */
  private loadedUserId: number | null | undefined = undefined;
  partner: SocialProfile | null = null;
  partnerLoading = false;
  partnerNotFound = false;
  messages: SocialMessage[] = [];
  messagesLoading = false;
  private messagesPage = 1;
  hasOlderMessages = false;
  loadingOlderMessages = false;
  /** Set when the thread itself can't be loaded/used (e.g. a block either direction). */
  threadError = '';

  draft = '';
  sending = false;
  sendError = '';
  private nextTempId = 0;

  /** While a thread is open, check for new messages a bit faster than the
   * header's own 30s badge poll (no websockets in this app - this is the
   * closest to "live" a plain page reachable). Only while the tab is visible. */
  private static readonly THREAD_POLL_MS = 8_000;
  private pollTimer: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private socialFeedService: SocialFeedService,
    private currentUserService: CurrentUserService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.currentUserService.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data) {
        this.currentUserId = data.id;
      }
      this.cdr.detectChanges();
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const raw = params.get('userId');
      const id = raw ? Number(raw) : null;
      // `undefined` (never loaded anything yet) vs `null` (the inbox route) are
      // deliberately distinct here - both activeUserId and a no-id route read as
      // `null`, so comparing straight to activeUserId would also swallow the very
      // first load of the inbox, not just a genuine repeat of the same route.
      if (id === this.loadedUserId) {
        return;
      }
      this.loadedUserId = id;
      this.stopPolling();
      this.activeUserId = id;
      if (id) {
        this.enterThread(id);
      } else {
        this.loadThreads();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  back(): void {
    if (this.activeUserId) {
      // replaceUrl, not a plain navigate: a plain navigate PUSHES a new
      // '/social-messages' history entry on top of the thread's own entry,
      // so the *next* Back (a real browser-history pop, below) lands on the
      // thread again instead of wherever came before it - Back and Back
      // ping-pong between the inbox and the thread forever instead of ever
      // leaving. Replacing the thread's entry with the inbox's avoids ever
      // creating that extra bounce-back point (works whether the thread was
      // reached from the inbox or straight from a profile's Message button).
      this.router.navigate(['/social-messages'], { replaceUrl: true });
      return;
    }
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/social-feed']);
    }
  }

  // -----------------------------------------------------------------------
  // Inbox
  // -----------------------------------------------------------------------

  /** Set only when the inbox itself failed to load - distinct from "no
   * conversations yet" (sg-empty), so a failure doesn't look like an
   * honestly empty inbox. */
  threadsError = false;

  loadThreads(): void {
    this.threadsLoading = true;
    this.threadsError = false;
    this.cdr.detectChanges(); // paint "Loading…" now, not just on the eventual next/error
    this.socialFeedService.getMessageThreads().subscribe({
      next: (page) => {
        this.threads = page.results;
        this.threadsPage = 1;
        this.hasMoreThreads = !!page.next;
        this.threadsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.threads = [];
        this.hasMoreThreads = false;
        this.threadsLoading = false;
        this.threadsError = true;
        this.cdr.detectChanges();
      },
    });
  }

  loadMoreThreads(): void {
    if (this.loadingMoreThreads || !this.hasMoreThreads) {
      return;
    }
    this.loadingMoreThreads = true;
    this.cdr.detectChanges();
    const next = this.threadsPage + 1;
    this.socialFeedService.getMessageThreads(next).subscribe({
      next: (page) => {
        const have = new Set(this.threads.map((t) => t.author.id));
        this.threads = [...this.threads, ...page.results.filter((t) => !have.has(t.author.id))];
        this.threadsPage = next;
        this.hasMoreThreads = !!page.next;
        this.loadingMoreThreads = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingMoreThreads = false;
        this.cdr.detectChanges();
      },
    });
  }

  openThread(userId: number): void {
    this.router.navigate(['/social-messages', userId]);
  }

  // -----------------------------------------------------------------------
  // A conversation
  // -----------------------------------------------------------------------

  private enterThread(userId: number): void {
    this.partner = null;
    this.partnerNotFound = false;
    this.messages = [];
    this.messagesPage = 1;
    this.hasOlderMessages = false;
    this.threadError = '';
    this.sendError = '';
    this.draft = '';

    this.partnerLoading = true;
    this.cdr.detectChanges(); // paint the cleared/loading state now, before either request resolves
    this.socialFeedService.getProfileByUserId(userId).subscribe({
      next: (profile) => {
        if (userId !== this.activeUserId) {
          return;
        }
        this.partner = profile;
        this.partnerLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        if (userId !== this.activeUserId) {
          return;
        }
        this.partnerNotFound = true;
        this.partnerLoading = false;
        this.cdr.detectChanges();
      },
    });

    this.loadMessages(userId);
    this.startPolling();
  }

  private loadMessages(userId: number): void {
    this.messagesLoading = true;
    this.cdr.detectChanges(); // paint "Loading…" now, not just on the eventual next/error
    this.socialFeedService.getMessages(userId).subscribe({
      next: (page) => {
        if (userId !== this.activeUserId) {
          return; // switched threads meanwhile
        }
        // The API pages newest-first (like notifications); reverse for a chat-style oldest-top display.
        this.messages = [...page.results].reverse();
        this.hasOlderMessages = !!page.next;
        this.messagesLoading = false;
        this.cdr.detectChanges();
        this.markRead(userId);
        this.scrollToBottom();
      },
      error: (err) => {
        if (userId !== this.activeUserId) {
          return;
        }
        this.threadError = err?.message || "Couldn't load this conversation.";
        this.messagesLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadOlderMessages(): void {
    const userId = this.activeUserId;
    if (!userId || this.loadingOlderMessages || !this.hasOlderMessages) {
      return;
    }
    this.loadingOlderMessages = true;
    this.cdr.detectChanges();
    const next = this.messagesPage + 1;
    this.socialFeedService.getMessages(userId, next).subscribe({
      next: (page) => {
        if (userId !== this.activeUserId) {
          return;
        }
        const older = [...page.results].reverse();
        const have = new Set(this.messages.map((m) => m.id));
        this.messages = [...older.filter((m) => !have.has(m.id)), ...this.messages];
        this.messagesPage = next;
        this.hasOlderMessages = !!page.next;
        this.loadingOlderMessages = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingOlderMessages = false;
        this.cdr.detectChanges();
      },
    });
  }

  private markRead(userId: number): void {
    this.socialFeedService.markThreadRead(userId).subscribe({ error: () => undefined });
  }

  private startPolling(): void {
    this.pollTimer = window.setInterval(() => this.pollNewMessages(), SocialMessagesComponent.THREAD_POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private pollNewMessages(): void {
    const userId = this.activeUserId;
    if (document.hidden || !userId || this.threadError) {
      return;
    }
    this.socialFeedService.getMessages(userId).subscribe({
      next: (page) => {
        if (userId !== this.activeUserId) {
          return;
        }
        const fresh = [...page.results].reverse();
        const have = new Set(this.messages.map((m) => m.id));
        const arrived = fresh.filter((m) => !have.has(m.id));
        if (arrived.length) {
          this.messages = [...this.messages, ...arrived];
          this.cdr.detectChanges();
          this.markRead(userId);
          this.scrollToBottom();
        }
      },
      error: () => undefined, // a failed check just means we try again next time
    });
  }

  // -----------------------------------------------------------------------
  // Sending
  // -----------------------------------------------------------------------

  send(): void {
    const userId = this.activeUserId;
    const text = this.draft.trim();
    if (!text || this.sending || !userId) {
      return;
    }
    this.sending = true;
    this.sendError = '';

    // Show it straight away (faded) so a slow request doesn't look like a dead click.
    const now = new Date().toISOString();
    const placeholder: SocialMessage = {
      id: --this.nextTempId,
      sender: { id: this.currentUserId ?? 0, name: 'You', avatar: null },
      content: text,
      created_at: now,
      read_at: null,
      is_mine: true,
      pending: true,
    };
    this.messages = [...this.messages, placeholder];
    this.draft = '';
    this.cdr.detectChanges();
    this.scrollToBottom();

    this.socialFeedService.sendMessage(userId, text).subscribe({
      next: (saved) => {
        if (userId !== this.activeUserId) {
          return;
        }
        this.messages = this.messages.map((m) => (m.id === placeholder.id ? saved : m));
        this.sending = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.messages = this.messages.filter((m) => m.id !== placeholder.id);
        // Give the text back (unless they've started typing something else) so nothing is lost.
        if (!this.draft) {
          this.draft = text;
        }
        this.sendError = err?.message || "Couldn't send. Please try again.";
        this.sending = false;
        this.cdr.detectChanges();
      },
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.host.nativeElement.querySelector('.sm-messages');
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 30);
  }

  // -----------------------------------------------------------------------
  // Display helpers
  // -----------------------------------------------------------------------

  openPartnerProfile(): void {
    if (this.activeUserId) {
      this.router.navigate(['/social-profile', this.activeUserId]);
    }
  }

  initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  avatarUrl(path: string | null): string {
    return this.socialFeedService.resolveImageUrl(path);
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }
}
